'use client';

/* eslint-disable max-lines-per-function */
import React, { useEffect, useRef, useState } from 'react';
import { MobileSearchOverlay } from '@/common/components/homeSearch/mobile-search-overlay.component';
import { SearchLoadingOverlay } from '@/common/components/homeSearch/search-loading-overlay.component';
import Input from '@/common/components/input/input.component';
import Spinner from '@/common/components/spinner/spinner.component';
import { useClickOutside } from '@/common/hooks/use-click-outside.hook';
import { useShowAppbarSearch } from '@/common/hooks/use-show-appbar-search.hook';
import CloseCircleFillIcon from '~/public/assets/images/svg/close-circule-fill.svg';
import SearchIcon from '~/public/assets/images/svg/search.svg';
import { useAppbarSearch } from './hooks/use-appbar-search';
import { SearchSuggestionsList } from './search-suggestions-list.component';

export const AppbarSearch = () => {
  const shouldShowSearch = useShowAppbarSearch(true);

  if (!shouldShowSearch) {
    return null;
  }

  return <AppbarSearchContent />;
};

function AppbarSearchContent() {
  const {
    goToSearchPage,
    beginNavigation,
    handleSearch,
    isLoading,
    isNavigating,
    searchResults,
    isOpen,
    setIsOpen,
    value,
    setValue,
    handleClearSearch,
  } = useAppbarSearch();

  const searchRef = useRef<HTMLDivElement>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const sync = () => {
      setIsMobileViewport(mediaQuery.matches);
    };
    sync();
    mediaQuery.addEventListener('change', sync);
    return () => {
      mediaQuery.removeEventListener('change', sync);
    };
  }, []);

  useClickOutside(searchRef, () => {
    if (isMobileViewport || isNavigating) return;
    setIsOpen(false);
  });

  const submitSearch = (text: string) => {
    goToSearchPage(text);
  };

  return (
    <div
      ref={searchRef}
      className="relative min-w-0 w-full max-w-[440px] flex-1"
    >
      <div className="hidden lg:block">
        <Input
          variant="filled"
          dir="rtl"
          className="search-appbar-input w-full rounded-xl !bg-gray-30 !text-right !text-sm hover:bg-gray-30 focus-within:!bg-gray-30"
          styles={{
            input: {
              textAlign: 'right',
              direction: 'rtl',
            },
          }}
          value={value}
          placeholder="جستجو..."
          onChange={(e) => {
            const newValue = e.target.value;
            setValue(newValue);
            if (newValue.length < 2) {
              setIsOpen(false);
            }
            handleSearch(newValue);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              submitSearch(value);
            }
          }}
          onFocus={() => {
            if (value.length >= 2 && searchResults?.data) {
              setIsOpen(true);
            }
          }}
          suffix={
            (isLoading && (
              <div className="h-5 w-5">
                <Spinner />
              </div>
            )) ||
            (value.length > 0 ? (
              <button
                type="button"
                aria-label="پاک کردن جستجو"
                className="h-5 w-5 cursor-pointer !p-0 !text-black-50 hover:text-black-50"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleClearSearch();
                }}
              >
                <CloseCircleFillIcon aria-hidden="true" />
              </button>
            ) : (
              <div className="h-5 w-5 text-black-200">
                <SearchIcon />
              </div>
            ))
          }
          classNames={{
            suffix: '!w-6 !h-6',
            input: '!text-right placeholder:!text-right',
          }}
        />
        {isOpen && !isNavigating && (
          <div className="absolute bottom-0 left-0 right-0 z-50 max-h-[548px] translate-y-full overflow-y-auto rounded-xl bg-white p-5 shadow-lg">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center gap-3 py-10">
                <Spinner
                  containerClassName="!w-auto !h-auto"
                  spinnerClassName="!h-8 !w-8 !text-plp-accent"
                />
                <p className="text-xs font-medium text-plp-secondary">
                  در حال جستجو...
                </p>
              </div>
            ) : (
              searchResults?.data && (
                <SearchSuggestionsList
                  compact
                  value={value}
                  data={searchResults.data}
                  onNavigate={() => {
                    beginNavigation();
                  }}
                />
              )
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        dir="rtl"
        className="flex h-10 w-full items-center gap-2 rounded-xl bg-gray-30 px-3 lg:hidden"
        onClick={() => {
          setIsOpen(true);
          if (value.length >= 2) {
            handleSearch(value);
          }
        }}
        aria-label="جستجو"
      >
        <span className="min-w-0 flex-1 truncate text-right text-sm text-black-200">
          {value || 'جستجو...'}
        </span>
        {isLoading ? (
          <Spinner
            containerClassName="!w-5 !h-5"
            spinnerClassName="!h-5 !w-5 !text-black-200"
          />
        ) : (
          <SearchIcon className="h-5 w-5 shrink-0 text-black-200" />
        )}
      </button>

      {isOpen && isMobileViewport && !isNavigating && (
        <MobileSearchOverlay
          value={value}
          isSearching={isLoading}
          searchResults={searchResults?.data}
          onClose={() => {
            setIsOpen(false);
          }}
          onClear={handleClearSearch}
          onSubmit={submitSearch}
          onChange={(e) => {
            const newValue = e.target.value;
            setValue(newValue);
            handleSearch(newValue);
          }}
          onNavigate={() => {
            beginNavigation();
          }}
        />
      )}

      {isNavigating && <SearchLoadingOverlay />}
    </div>
  );
}
